import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getStructureSummary, importPositionHeights, type HeightsImportSummary } from "../../../api/rest";
import { NominalHeightField } from "../../NominalHeightField";
import { useSiteId } from "../../../hooks/useSiteId";
import css from "../../WarehouseAdminPage.module.css";

/**
 * ADR 186: upload the per-position measured-heights spreadsheet ("Tamanho do Local",
 * .xlsx as-is or a CSV export). Server-side it sets clear heights, backfills levels and
 * stacks elevations; the 3D rack picks the measurements up on the next /live load
 * (the volumes fetch is per-mount, so no cache to invalidate here).
 */
export function HeightsSection() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const { warehouseId = "" } = useParams();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [summary, setSummary] = useState<HeightsImportSummary | null>(null);
  const queryClient = useQueryClient();

  // ADR 322: the warehouse's standing count of formula-derived elevations. Shares the
  // /live gate's cache key, so opening this tab costs nothing extra.
  const structure = useQuery({
    queryKey: ["structure-summary", siteId, warehouseId],
    queryFn: () => getStructureSummary(siteId, warehouseId),
    enabled: !!siteId && !!warehouseId,
    staleTime: 60_000,
  });
  const estimated = structure.data?.elevationEstimated ?? 0;

  async function handleUpload(file: File) {
    setBusy(true);
    setErr(null);
    setSummary(null);
    try {
      setSummary(await importPositionHeights(siteId, warehouseId, file));
      // The import moves positions off the formula; the count above must not lag it.
      queryClient.invalidateQueries({ queryKey: ["structure-summary", siteId, warehouseId] });
    } catch (x) {
      setErr(x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <>
      <p className={css.note}>{t("structure.heights.note")}</p>
      {/* ADR 322: how many rack positions sit where the nominal formula says rather than
          where anybody measured. Visible without uploading anything — the Flex columns
          that rendered a metre low (ADR 321) were counted at import time and never shown. */}
      {estimated > 0 && (
        <p className={css.note}>
          {t("structure.heights.estimatedElevations", {
            count: estimated,
            total: structure.data?.rackVolumes ?? 0,
          })}
        </p>
      )}
      <div className={css.form}>
        <label className={css.fileLabel}>
          {busy ? t("structure.heights.importing") : t("structure.heights.import")}
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv"
            hidden
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
          />
        </label>
        {/* ADR 226: the file is the exception, not the rule — the nominal sits
            beside it so the common "no file, one known height" case is one field. */}
        <NominalHeightField siteId={siteId} warehouseId={warehouseId} />
      </div>
      {err && <p className={css.resultError} role="alert">{err}</p>}
      {summary && (
        <div
          className={summary.unmatchedAddresses > 0 || summary.rejects.length > 0 || summary.elevationSkipped > 0
            ? css.resultWarn : css.resultOk}
          role="status"
        >
          <p>
            {t("structure.heights.done", {
              matched: summary.matchedAddresses,
              locations: summary.matchedLocations,
              elevations: summary.elevationsSet,
            })}
          </p>
          {/* ADR 322: the import refuses to invent an elevation for a column whose lower
              levels the file does not carry (ADR 186), and until now said so only in a
              number nobody rendered. Those positions fall back to the nominal formula. */}
          {summary.elevationSkipped > 0 && (
            <p>{t("structure.heights.elevationSkipped", { count: summary.elevationSkipped })}</p>
          )}
          {summary.unmatchedAddresses > 0 && (
            <p>
              {t("structure.heights.unmatched", {
                count: summary.unmatchedAddresses,
                sample: summary.unmatchedSample.slice(0, 5).join(", "),
              })}
            </p>
          )}
          {summary.rejects.length > 0 && (
            <ul className={css.rejectList}>
              {summary.rejects.slice(0, 10).map((r, i) => (
                <li key={i}>{r.line} — {r.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
